const propertyMap: Record<string, string> = {
    fontFamily: '--kgr-font-family-base',
    fontFamilyHeadlines: '--kgr-headings-font-family',
    colorWhite: '--kgr-color-white',
    colorBlack: '--kgr-color-black',
    colorGrayDarker: '--kgr-color-gray-darker',
    colorGrayDark: '--kgr-color-gray-dark',
    colorGray: '--kgr-color-gray',
    colorGrayMedium: '--kgr-color-gray-medium',
    colorGrayLight: '--kgr-color-gray-light',
    colorGrayLighter: '--kgr-color-gray-lighter',
    colorAccent: '--kgr-color-accent',
    colorAccentAlternate: '--kgr-color-accent-alternate',
    colorAccentInverted: '--kgr-color-accent-inverted',
    colorSuccess: '--kgr-color-success',
    colorSuccessInverted: '--kgr-color-success-inverted',
    colorWarning: '--kgr-color-warning',
    colorWarningInverted: '--kgr-color-warning-inverted',
    colorError: '--kgr-color-error',
    colorErrorInverted: '--kgr-color-error-inverted',
    colorHighlight: '--kgr-color-highlight',
    colorHighlightInverted: '--kgr-color-highlight-inverted',
};

function applyPalette(theme: Record<string, string>): void {
    const style = document.documentElement.style;

    for (const [key, cssVar] of Object.entries(propertyMap)) {
        const value = theme[key];
        if (value) {
            style.setProperty(cssVar, value);
        } else {
            style.removeProperty(cssVar);
        }
    }
}

/**
 * Normalizes a CSS font-family value to the class name format used by the styleguide.
 * E.g. `'Forum', Arial, sans-serif` → `forum`, `'Open Sans', Arial` → `openSans`
 * Mirrors PHP Theme::normalizeFontFamily().
 */
function normalizeFontFamily(fontFamily: string): string {
    // Take only the first font from the stack (before the comma)
    const firstFont = fontFamily.split(',')[0].trim().replace(/['"]/g, '');
    // ucwords equivalent: capitalize first letter of each word
    const ucwords = firstFont.replace(/\b\w/g, (c) => c.toUpperCase());
    // Remove spaces
    const noSpaces = ucwords.replace(/ /g, '');
    // lcfirst: lowercase first character
    return noSpaces.charAt(0).toLowerCase() + noSpaces.slice(1);
}

/**
 * Reads the default font from CSS custom properties and applies the corresponding
 * font classes to <html>. This activates the correct fontWeights mixin for the
 * client's configured font (defined in their styleguide _dynamicSettings.scss).
 *
 * Call early in main.ts (after CSS imports) so the correct font weights are
 * applied from the first paint, even before login.
 */
export function initTheme(): void {
    const computed = getComputedStyle(document.documentElement);
    const fontBase = computed.getPropertyValue('--kgr-font-family-base').trim();
    const fontHeadlines = computed.getPropertyValue('--kgr-headings-font-family').trim();

    const prefix = 'kgr-font--';
    if (fontBase) {
        document.documentElement.classList.add(`${prefix}${normalizeFontFamily(fontBase)}`);
    }
    if (fontHeadlines) {
        document.documentElement.classList.add(`${prefix}${normalizeFontFamily(fontHeadlines)}Headings`);
    }
}

export function applyTheme(theme: Record<string, string> = {}): void {
    const style = document.documentElement.style;

    applyPalette(theme);

    style.setProperty('--kgr-inputGroup-label-text-transform', theme.formLabelUppercase === 'true' ? 'uppercase' : 'none');

    if (theme.fontSize) {
        style.setProperty('--kgr-font-size-base', `${theme.fontSize}px`);
    }

    const prefix = 'kgr-font--';
    for (const cls of [...document.documentElement.classList]) {
        if (cls.startsWith(prefix)) {
            document.documentElement.classList.remove(cls);
        }
    }

    // Read current fonts from CSS custom properties as fallback (set by client styleguide).
    // The API theme overrides these if present.
    const computed = getComputedStyle(document.documentElement);
    const cssFont = normalizeFontFamily(computed.getPropertyValue('--kgr-font-family-base').trim() || 'Poppins');
    const cssFontHeadlines = normalizeFontFamily(computed.getPropertyValue('--kgr-headings-font-family').trim() || 'Poppins');

    const fontBase = theme.fontFamilyNormalized ?? cssFont;
    const fontHeadlines = theme.fontFamilyHeadlinesNormalized ?? cssFontHeadlines;

    document.documentElement.classList.add(`${prefix}${fontBase}`);
    document.documentElement.classList.add(`${prefix}${fontHeadlines}Headings`);

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute('content', theme.colorAccent ?? '#000870');
    }
}

/**
 * Angular `ThemeService.hideTabbar()` / `unHideTabbar()`: the full-screen offering modals collapse
 * the app tabbar while they are open by zeroing its layout custom properties.
 */
export function hideTabbar(): void {
    const style = document.documentElement.style;
    style.setProperty('--kgr-pageLayout-footer-height', '0px');
    style.setProperty('--kgr-tabbar-height', '0px');
}

export function unHideTabbar(): void {
    const style = document.documentElement.style;
    style.removeProperty('--kgr-pageLayout-footer-height');
    style.removeProperty('--kgr-tabbar-height');
}
